import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Upload,
  Loader2,
  CheckCircle,
  Video,
  FileVideo,
  AlertCircle,
} from "lucide-react";

const RecruitmentTrial = () => {
  const { candidateId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [uploading, setUploading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [error, setError] = useState(null);

  // Fetch current candidate status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        // We don't have a public endpoint for this, so we start empty
        // Videos will be tracked locally after upload
      } catch {
        setError("Lien invalide");
      }
    };
    checkStatus();
  }, [candidateId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("video/")) {
      toast.error("Veuillez sélectionner un fichier vidéo");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 200 Mo)");
      return;
    }
    if (videos.length >= 2) {
      toast.error("Vous avez déjà déposé 2 vidéos");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(
        `${API}/recruitment/candidates/${candidateId}/upload-video?token=${encodeURIComponent(token)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setVideos((prev) => [...prev, { name: file.name, size: (file.size / (1024 * 1024)).toFixed(1) }]);
      toast.success(`Vidéo ${res.data.total_videos}/2 déposée avec succès !`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
      if (err.response?.status === 403) {
        setError("Lien invalide ou expiré");
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold mb-3">Accès refusé</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const allUploaded = videos.length >= 2;

  return (
    <div className="min-h-screen bg-background" data-testid="recruitment-trial-page">
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Video className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-heading font-bold mb-3">Phase d'essai</h1>
          <p className="text-muted-foreground text-lg">
            Déposez vos 2 vidéos de démonstration pour valider votre candidature.
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-8">
          <h2 className="font-heading font-bold text-lg mb-4">Instructions</h2>
          <div className="space-y-3 mb-8 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">1</span>
              </div>
              <div>
                <p className="font-medium">Vidéo 1 — Présentation d'un concept</p>
                <p className="text-muted-foreground">Présentez un concept clé de votre domaine d'expertise (5-15 min)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">2</span>
              </div>
              <div>
                <p className="font-medium">Vidéo 2 — Mini-tutoriel pratique</p>
                <p className="text-muted-foreground">Faites un tutoriel pratique montrant votre pédagogie (5-15 min)</p>
              </div>
            </div>
          </div>

          {/* Uploaded videos */}
          {videos.length > 0 && (
            <div className="mb-6 space-y-3">
              {videos.map((v, i) => (
                <div key={i} className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{v.name}</p>
                    <p className="text-xs text-muted-foreground">{v.size} Mo</p>
                  </div>
                  <FileVideo className="w-5 h-5 text-muted-foreground" />
                </div>
              ))}
            </div>
          )}

          {allUploaded ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-bold mb-2">Vidéos déposées ! 🎉</h3>
              <p className="text-muted-foreground">
                Merci ! Notre équipe examinera vos vidéos et vous contactera par email.
              </p>
            </div>
          ) : (
            <div className="text-center">
              <input
                id="video-upload-input"
                type="file"
                accept="video/*"
                onChange={handleUpload}
                disabled={uploading}
                className="hidden"
              />
              <Button
                size="lg"
                className="glow-green"
                disabled={uploading}
                onClick={() => document.getElementById("video-upload-input").click()}
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-5 h-5 mr-2" />
                )}
                {uploading ? "Upload en cours..." : `Déposer la vidéo ${videos.length + 1}`}
              </Button>
              <p className="text-xs text-muted-foreground mt-3">
                Formats : MP4, WebM, MOV, AVI — Max 200 Mo par vidéo
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecruitmentTrial;
